import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { dashboardStatusHexColors, getItemTypeColor } from '../../../../constants/integrationTypes';
import { useResourcesService } from '../../../../services';
import { useJwt } from '../../../../helpers/common';
import CustomTooltip from '../../../../components/common/CustomTooltip';

interface StoryPointsData {
  total: number;
  computed: number;
  totalTasks: number;
  completedStoryPoints?: number;
  completedStoryPointsComputed?: number;
  perTeam: Record<string, {
    total: number;
    computed: number;
    taskCount: number;
    teamName: string;
    projectName: string;
    sprintName: string;
    taskBreakdown: Record<string, number>;
    statusBreakdown: Record<string, number>;
    completedStoryPoints?: number;
    completedStoryPointsComputed?: number;
  }>;
  taskBreakdown?: Record<string, number>;
  statusBreakdown?: Record<string, number>;
}

interface OriginalEstimateData {
  total: number;
  computed: number;
  completed: number;
  completedComputed?: number;
  perTeam: Record<string, {
    total: number;
    computed: number;
    completed: number;
    completedComputed?: number;
    teamName: string;
    projectName: string;
    sprintName: string;
  }>;
}

interface StoryPointsTooltipProps {
  resourceName: string;
  storyPointsData: StoryPointsData | null;
  originalEstimateData?: OriginalEstimateData | null;
  skills?: string[];
  isOptionalResource?: boolean;
  showImportCTA?: boolean;
  resourceId?: string;
  selectedTeams?: string[];
  onViewTasks?: () => void;
}

const StoryPointsTooltip: React.FC<StoryPointsTooltipProps> = ({
  resourceName,
  storyPointsData,
  originalEstimateData,
  isOptionalResource = false,
  showImportCTA = false,
  resourceId,
  selectedTeams = [],
  onViewTasks,
}) => {
  const { orgDetails, teams: organizationTeams } = useSelector((state: any) => state.organization);
  const { addTeamsToResource } = useResourcesService();
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const hoursPerDay = orgDetails?.hours_per_day || 6; // Default to 6 if not set

  const hasStoryPoints = storyPointsData && storyPointsData.total > 0;
  // Skills section removed as per request

  const total = storyPointsData?.total || 0;
  const computed = storyPointsData?.computed || 0;
  const totalTasks = storyPointsData?.totalTasks || 0;
  const perTeam = storyPointsData?.perTeam || {};
  const taskBreakdown = storyPointsData?.taskBreakdown || {};
  const statusBreakdown = storyPointsData?.statusBreakdown || {};

  const teamEntries = Object.entries(perTeam);
  const hasMultipleTeams = teamEntries.length > 1;
  const hasTaskBreakdown = Object.keys(taskBreakdown).length > 0;
  const hasStatusBreakdown = Object.keys(statusBreakdown).length > 0;

  // Get selected team information - only when exactly 1 team is selected
  // This ensures the "Add to team" message and button only show when we know which team to add to
  const selectedTeamData = React.useMemo(() => {
    if (!selectedTeams || selectedTeams.length !== 1 || !organizationTeams) return null;
    const teamId = selectedTeams[0];
    return organizationTeams.find((team: any) => String(team.id) === String(teamId));
  }, [selectedTeams, organizationTeams]);

  const handleImportResource = useCallback(async () => {
    if (!resourceId || !selectedTeamData || !org_id) {
      console.error('Missing required data for adding resource to team');
      return;
    }

    try {
      // Call add_teams_to_resource API
      await addTeamsToResource({
        resource_id: Number(resourceId),
        team_id: [selectedTeamData.id]
      });

      // Only refresh capacity planning data - don't refresh all resources to avoid UI jitter
      // The capacity planning API will return updated data with the resource now in the team
      window.dispatchEvent(new Event('capacity-planning:refresh'));
    } catch (error) {
      console.error('Error adding resource to team:', error);
    }
  }, [resourceId, selectedTeamData, org_id]);

  // Calculate total tasks for breakdowns to compute percentages
  // Use totalTasks from data if available, otherwise calculate
  const displayTotalTasks = totalTasks || Object.values(taskBreakdown).reduce((a, b) => a + b, 0);
  const totalStatusTasks = (statusBreakdown.total as number) || Object.values(statusBreakdown).reduce((a, b) => typeof b === 'number' ? a + b : a, 0);

  return (
    <div className="p-3 max-w-[340px]">
      {/* Header */}
      <div className="text-sm font-bold text-slate-900 dark:text-white mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          {/* Show circle-alert only when optional resource AND exactly 1 team selected */}
          {isOptionalResource && selectedTeamData && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-yellow-500 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" x2="12" y1="8" y2="12"></line>
              <line x1="12" x2="12.01" y1="16" y2="16"></line>
            </svg>
          )}
          <span>{resourceName}</span>
        </div>
        {isOptionalResource && selectedTeamData && (
          <div className="text-xs text-slate-600 dark:text-slate-300 font-normal mt-1.5 leading-relaxed">
            This person has work items assigned in this sprint but is not currently part of <span className="font-semibold">{selectedTeamData.name}</span>
            {selectedTeamData.projects?.[0]?.project_name && (
              <span> ({selectedTeamData.projects[0].project_name})</span>
            )}
            . Add them to the team to track their capacity and workload.
          </div>
        )}
      </div>

      {/* Total Work Items */}
      <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center mb-1">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Total Work Items
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-slate-900 dark:text-white">
              {displayTotalTasks}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">items</span>
          </div>
        </div>
      </div>

      {/* Story Points & Original Estimate Section */}
      <div className="mb-3">
        <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
          Effort Metrics
        </div>

        {/* Story Points Row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">Story Points</span>
            <CustomTooltip
              title="Maps to Story Points field in Azure DevOps for User Story, Bug, and Spike work items. 'Completed' shows the Actual Story Points value for that work item."
              placement="top"
              arrow
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-2.5 h-2.5 text-slate-400 dark:text-slate-500 cursor-help"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </CustomTooltip>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-sm font-semibold ${hasStoryPoints ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
              {total.toFixed(1)}
              <CustomTooltip
                title={computed > 0 ? `${computed.toFixed(1)} SP computed by Cadence Pro` : 'All SP from Azure DevOps'}
                placement="top"
                arrow
              >
                <span className={`text-[9px] ml-0.5 cursor-help ${computed > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
              </CustomTooltip>
            </span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <span className={`text-sm font-semibold ${((storyPointsData as any)?.completedStoryPoints || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
              {((storyPointsData as any)?.completedStoryPoints || 0).toFixed(1)}
              <CustomTooltip
                title={((storyPointsData as any)?.completedStoryPointsComputed || 0) > 0 ? `${((storyPointsData as any)?.completedStoryPointsComputed || 0).toFixed(1)} Actual SP computed by Cadence Pro` : 'All Actual SP from Azure DevOps'}
                placement="top"
                arrow
              >
                <span className={`text-[9px] ml-0.5 cursor-help ${((storyPointsData as any)?.completedStoryPointsComputed || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
              </CustomTooltip>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-normal ml-1">Completed</span>
            </span>
          </div>
        </div>

        {/* Original Estimate Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">Original Estimate</span>
            <CustomTooltip
              title="Maps to Original Estimate field in Azure DevOps for Task work items. 'Completed' shows the actual effort recorded when the task was finished." placement="top"
              arrow
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-2.5 h-2.5 text-slate-400 dark:text-slate-500 cursor-help"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </CustomTooltip>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-sm font-semibold ${(originalEstimateData?.total || 0) > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
              {(originalEstimateData?.total || 0).toFixed(1)}h
              <CustomTooltip
                title={originalEstimateData && originalEstimateData.computed > 0 ? `${originalEstimateData.computed.toFixed(1)} hrs computed by Cadence Pro` : 'All Original Estimates from Azure DevOps'}
                placement="top"
                arrow
              >
                <span className={`text-[9px] ml-0.5 cursor-help ${originalEstimateData && originalEstimateData.computed > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
              </CustomTooltip>
            </span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <span className={`text-sm font-semibold ${(originalEstimateData?.completed || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
              {(originalEstimateData?.completed || 0).toFixed(1)}h
              <CustomTooltip
                title={(originalEstimateData?.completedComputed || 0) > 0 ? `${(originalEstimateData?.completedComputed || 0).toFixed(1)} completed hrs computed by Cadence Pro` : 'All completed estimates from Azure DevOps'}
                placement="top"
                arrow
              >
                <span className={`text-[9px] ml-0.5 cursor-help ${(originalEstimateData?.completedComputed || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
              </CustomTooltip>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 font-normal ml-1">Completed</span>
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-2">
        {onViewTasks && displayTotalTasks > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewTasks();
            }}
            className="w-full px-3 py-1.5 text-[11px] font-medium text-white bg-primary-light hover:bg-primary-hover rounded transition-colors duration-200 flex items-center justify-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            View All Work Items
          </button>
        )}
        {isOptionalResource && showImportCTA && selectedTeamData && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleImportResource();
            }}
            className="w-full px-3 py-1.5 text-[11px] font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 rounded transition-colors duration-200 flex items-center justify-center gap-0.5"
          >
            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add to {selectedTeamData.name}
            {selectedTeamData.projects?.[0]?.project_name && (
              <span> ({selectedTeamData.projects[0].project_name})</span>
            )} team
          </button>
        )}
      </div>

      {/* Separator line before Status Breakdown */}
      {(hasStatusBreakdown || hasTaskBreakdown || teamEntries.length > 0) && (
        <div className="border-t border-slate-200 dark:border-slate-700 mt-2 mb-3"></div>
      )}

      {/* Status Breakdown */}
      {hasStatusBreakdown && (
        <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
            Status Breakdown
          </div>
          <div className="space-y-2">
            {Object.entries(statusBreakdown)
              .filter(([key]) => key !== 'total')
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(([status, count]) => {
                const percentage = totalStatusTasks > 0 ? ((count as number) / totalStatusTasks) * 100 : 0;
                const color = dashboardStatusHexColors[status] || '#9ca3af';

                return (
                  <div key={status} className="flex flex-col w-full">
                    <div className="flex justify-between items-center mb-1 text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }}></span>
                        <span className="text-slate-600 dark:text-slate-300 capitalize">{status.replace('_', ' ')}</span>
                      </div>
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{count}</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Task Breakdown */}
      {hasTaskBreakdown && (
        <div className={`${teamEntries.length > 0 ? 'mb-3 pb-2 border-b border-slate-200 dark:border-slate-700' : ''}`}>
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
            Work Item Types ({displayTotalTasks})
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(taskBreakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => {
                return (
                  <div key={type} className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium ${getItemTypeColor(type)}`}>
                    <span className="capitalize truncate max-w-[80px]" title={type}>
                      {type}
                    </span>
                    <span className="font-bold border-l border-current pl-1.5 ml-0.5 opacity-80">{count}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Teams Breakdown */}
      {teamEntries.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Team Allocation
          </div>

          <div className={`space-y-1.5 ${hasMultipleTeams ? 'max-h-[160px] overflow-y-auto pr-1 custom-scrollbar' : ''}`}>
            {teamEntries.map(([key, teamData]) => {
              let { teamName, projectName, sprintName, total: teamTotal, taskCount, taskBreakdown: teamTaskBreakdown, statusBreakdown: teamStatusBreakdown } = teamData;

              // Parse path if it contains backslashes (format: Workspace\\2025\\Q2\\1.5.8 (20250501))
              // First part = project name, Last part = sprint name
              // Check both key and teamName for the path format
              const pathToParse = key.includes('\\') ? key : (teamName?.includes('\\') ? teamName : null);

              if (pathToParse) {
                const pathParts = pathToParse.split('\\').map(p => p.trim()).filter(Boolean);
                if (pathParts.length > 0) {
                  // First part is project name
                  if (!projectName && pathParts[0]) {
                    projectName = pathParts[0];
                  }
                  // Last part is sprint name
                  if (!sprintName && pathParts.length > 1 && pathParts[pathParts.length - 1]) {
                    sprintName = pathParts[pathParts.length - 1];
                  }
                }
              }

              return (
                <div
                  key={key}
                  className="bg-slate-50 dark:bg-slate-800/50 rounded p-2 border border-slate-100 dark:border-slate-700"
                >
                  {/* Team Header */}
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate" title={`${teamName} ${projectName ? `(${projectName})` : ''}`}>
                        {teamName} <span className="font-normal text-slate-500 dark:text-slate-400">{projectName ? `(${projectName})` : ''}</span>
                      </div>
                      {sprintName ? (
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] text-slate-400">Sprint:</span>
                          <span className="text-[9px] text-slate-600 dark:text-slate-300 font-medium truncate" title={sprintName}>
                            {sprintName}
                          </span>
                        </div>
                      ) : (
                        <div className="text-[9px] text-slate-400 italic">No sprint</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 bg-white dark:bg-slate-700 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-600">
                      <span className="text-[10px] font-bold text-slate-900 dark:text-white">{taskCount}</span>
                      <span className="text-[9px] text-slate-500 dark:text-slate-400 font-medium">items</span>
                    </div>
                  </div>

                  {/* Effort Metrics per Team */}
                  <div className="mt-1.5 pt-1.5 border-t border-dashed border-slate-200 dark:border-slate-700/50">
                    {/* Story Points Row */}
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-0.5">
                        <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Story Points</span>
                        <CustomTooltip
                          title="Maps to Story Points field in Azure DevOps for User Story, Bug, and Spike work items. 'Completed' shows the Actual Story Points value for that work item."
                          placement="top"
                          arrow
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-2 h-2 text-slate-400 dark:text-slate-500 cursor-help"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 16v-4"></path>
                            <path d="M12 8h.01"></path>
                          </svg>
                        </CustomTooltip>
                      </div>
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className={`text-xs font-bold ${teamTotal > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                          {teamTotal.toFixed(1)}
                          <CustomTooltip
                            title={(teamData as any)?.computed > 0 ? `${((teamData as any)?.computed || 0).toFixed(1)} SP computed by Cadence Pro` : 'All SP from Azure DevOps'}
                            placement="top"
                            arrow
                          >
                            <span className={`text-[8px] ml-0.5 cursor-help ${(teamData as any)?.computed > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                          </CustomTooltip>
                        </span>
                        <span className="text-slate-300 dark:text-slate-600">|</span>
                        <span className={`text-xs font-bold ${((teamData as any)?.completedStoryPoints || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
                          {((teamData as any)?.completedStoryPoints || 0).toFixed(1)}
                          <CustomTooltip
                            title={((teamData as any)?.completedStoryPointsComputed || 0) > 0 ? `${((teamData as any)?.completedStoryPointsComputed || 0).toFixed(1)} Actual SP computed by Cadence Pro` : 'All Actual SP from Azure DevOps'}
                            placement="top"
                            arrow
                          >
                            <span className={`text-[8px] ml-0.5 cursor-help ${((teamData as any)?.completedStoryPointsComputed || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                          </CustomTooltip>
                          <span className="text-[8px] text-slate-500 dark:text-slate-400 font-normal ml-0.5">Completed</span>
                        </span>
                      </div>
                    </div>

                    {/* Original Estimate Row */}
                    {(() => {
                      const teamOEData = originalEstimateData?.perTeam?.[key];
                      const teamOETotal = teamOEData?.total || 0;
                      const teamOECompleted = teamOEData?.completed || 0;
                      const teamOEComputed = teamOEData?.computed || 0;
                      const teamOECompletedComputed = teamOEData?.completedComputed || 0;
                      return (
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-0.5">
                            <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Original Estimate</span>
                            <CustomTooltip
                              title="Maps to Original Estimate field in Azure DevOps for Task work items. 'Completed' shows the actual effort recorded when the task was finished."
                              placement="top"
                              arrow
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-2 h-2 text-slate-400 dark:text-slate-500 cursor-help"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M12 16v-4"></path>
                                <path d="M12 8h.01"></path>
                              </svg>
                            </CustomTooltip>
                          </div>
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className={`text-xs font-bold ${teamOETotal > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                              {teamOETotal.toFixed(1)}h
                              <CustomTooltip
                                title={teamOEComputed > 0 ? `${teamOEComputed.toFixed(1)} hrs computed by Cadence Pro` : 'All Original Estimates from Azure DevOps'}
                                placement="top"
                                arrow
                              >
                                <span className={`text-[8px] ml-0.5 cursor-help ${teamOEComputed > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                              </CustomTooltip>
                            </span>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span className={`text-xs font-bold ${teamOECompleted > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
                              {teamOECompleted.toFixed(1)}h
                              <CustomTooltip
                                title={teamOECompletedComputed > 0 ? `${teamOECompletedComputed.toFixed(1)} completed hrs computed by Cadence Pro` : 'All completed estimates from Azure DevOps'}
                                placement="top"
                                arrow
                              >
                                <span className={`text-[8px] ml-0.5 cursor-help ${teamOECompletedComputed > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                              </CustomTooltip>
                              <span className="text-[8px] text-slate-500 dark:text-slate-400 font-normal ml-0.5">Completed</span>
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Task Breakdown per Team */}
                  {teamTaskBreakdown && Object.keys(teamTaskBreakdown).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-slate-200 dark:border-slate-700/50 border-dashed">
                      {Object.entries(teamTaskBreakdown).map(([type, count]) => (
                        <span key={type} className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${getItemTypeColor(type)}`}>
                          {count} {type}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Status Breakdown per Team */}
                  {teamStatusBreakdown && Object.keys(teamStatusBreakdown).length > 0 && (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1">
                      {Object.entries(teamStatusBreakdown).map(([status, count]) => (
                        <span key={status} className="text-[9px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dashboardStatusHexColors[status] || '#9ca3af' }}></span>
                          {count} {status.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Note */}
      {hasStoryPoints && (
        <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700">
          <div className="text-[9px] text-slate-400 dark:text-slate-500 leading-relaxed italic">
            * Story points and Original Estimate include computed values. Work items without assigned story points or original estimates will set to default value .
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryPointsTooltip;

